import React, { useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { canAccessAdministration } from "../utils/appRole";
import {
  ScanLine,
  Radio,
  Footprints,
  Brain,
  History,
  FileBarChart,
  Settings,
  Bell,
} from "lucide-react";
import { LIVE_NAME, LIVE_PATH, LEGACY_LIVE_PATH } from "../config/liveName";

/**
 * Navigation menu items configuration
 */
const menuItemsData = [
  {
    id: "wankhede360.read",
    label: LIVE_NAME,
    icon: <ScanLine />,
    path: LIVE_PATH,
  },
  {
    id: "digitalpatrol.read",
    label: "Digital Patrol",
    icon: <Footprints />,
    path: "/digital-patrol",
  },
  {
    id: "livemonitoring.read",
    label: "Live Monitoring",
    icon: <Radio />,
    path: "/live-monitoring",
  },
  // {
  //   id: "alert-services.read",
  //   label: "Alert Services",
  //   icon: <Bell />,
  //   path: "/alert-services",
  // },
  {
    id: "aianalysis.read",
    label: "AI Analysis",
    icon: <Brain />,
    path: "/ai-analysis",
  },
  {
    id: "flashback.read",
    label: "Flashback",
    icon: <History />,
    path: "/flashback",
  },
  // {
  //   id: "smartreports.read",
  //   label: "Smart Reports",
  //   icon: <FileBarChart />,
  //   path: "/smart-reports",
  // },
  {
    id: "administration.read",
    label: "Administration",
    icon: <Settings />,
    path: "/administration",
    activePathPrefix: "/administration",
  },
  // {
  //   id: "constructionsitemanagement.read",
  //   label: "Construction Site Management",
  //   path: "/construction-site-management",
  // },

  // {
  //   id: "usermanagement.read",
  //   label: "Users",
  //   path: "/user-management/users",
  // },
];

const Sidebar = ({ collapsed, onToggle }) => {
  const accountAccess = useSelector((state) => state.auth.accountAccess);
  const accessLoaded = useSelector((state) => state.auth.accessLoaded);
  const showAdministration =
    accessLoaded && canAccessAdministration(accountAccess);
  const menuItems = useMemo(
    () =>
      menuItemsData
        .filter(
          (item) =>
            item.id !== "administration.read" || showAdministration,
        )
        .map((item) => ({ ...item, visible: true })),
    [showAdministration],
  );
  const navigate = useNavigate();
  const location = useLocation();

  const handleMenuClick = (item) => {
    navigate(item.path);
  };

  const isActive = (item) => {
    const path = item.path;
    if (path === LIVE_PATH) {
      return (
        location.pathname === LIVE_PATH ||
        location.pathname === LEGACY_LIVE_PATH
      );
    }
    if (item.activePathPrefix) {
      return (
        location.pathname === item.path ||
        location.pathname.startsWith(`${item.activePathPrefix}/`)
      );
    }
    return location.pathname === path;
  };

  return (
    <>
      {/* INTERNAL CSS */}
      <style>{`
        .sidebar {
          width: 240px;
          height: 100vh;
          background-color: #212121;
          color: #FFFFFF;
          display: flex;
          flex-direction: column;
          border-right: 0.8px solid rgba(255, 255, 255, 0.12);
          transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          overflow-x: hidden;
        }

        .sidebar.collapsed {
          width: 60px;
        }

        /* Header */
        .sidebar-header {
          height: 68px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          padding: 0 14px;
        }

        .logo-image {
          width: 120px;
          height: 48px;
          object-fit: contain;
          transition: all 0.3s ease;
        }
        .sidebar.collapsed .logo-image {
          width: 36px;
          height: 36px;
        }

        /* Toggle Arrow */
        .toggle-btn {
          position: absolute;
          right: -12px;
          bottom: 8px;
          width: 32px;
          height: 32px;
          background-color: #2932E5;
          color: white;
          border: none;
          border-radius: 50%;
          cursor: pointer;
          display: none;
          justify-content: center;
          align-items: center;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
        .sidebar:hover .toggle-btn {
          display: flex;
        }

        /* Navigation */
        .nav-list {
          list-style: none;
          padding: 6px 8px;
          margin: 0;
          flex: 1;
        }

        .nav-item {
          margin-bottom: 4px;
        }

        .nav-link {
          width: 100%;
          display: flex;
          align-items: center;
          background: transparent;
          border: none;
          padding: 12px 14px;
          border-radius: 4px;
          cursor: pointer;
          transition: background 0.25s ease;
          position: relative;
          color: #FFFFFF;
          justify-content: flex-start;
        }

        .sidebar.collapsed .nav-link {
          justify-content: center;
          padding: 12px 0px;
        }

        .nav-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          margin-right: 10px;
          
        }
        .sidebar.collapsed .nav-icon {
          margin-right: 0;
        }
        .nav-label {
          margin: 0px;
    font-weight: 400;
    line-height: 1.5;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    font-family: Roboto, sans-serif;
    color: rgb(255, 255, 255);
        }

        .nav-link:hover {
          background-color: rgba(255, 255, 255, 0.05);
        }

        .nav-link.active {
          background-color: rgba(255, 151, 111, 0.3);
        }

        .nav-link.active:hover {
          background-color: rgba(255, 151, 111, 0.3);
        }

        .disabled_new_class {
          opacity: 0.4;
          pointer-events: none;
        }

        /* Footer */
        .sidebar-footer {
          padding: 12px 16px;
          border-top: 1px solid rgba(255,255,255,0.12);
        }

        .footer-text {
          font-size: 12px;
          opacity: 0.7;
        }
      `}</style>

      {/* SIDEBAR HTML */}
      <div className={`sidebar ${collapsed ? "collapsed" : ""}`}>
        <div className="sidebar-header">
          <img
            src="/images/omni_logo.png"
            alt="OomniEye Logo"
            className="logo-image"
          />

          <button className="toggle-btn" onClick={onToggle}>
            {collapsed ? ">" : "<"}
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul className="nav-list">
            {menuItems?.map((item) => (
              <li key={item.id} className="nav-item">
                <button
                  disabled={!item.visible}
                  className={`nav-link ${isActive(item) ? "active" : ""} ${
                    !item.visible ? "disabled_new_class" : ""
                  }`}
                  onClick={() => handleMenuClick(item)}
                  title={collapsed ? item.label : ""}
                >
                  <span className="nav-icon">
                    {React.cloneElement(item.icon, {
                      size: 20,
                      color: "#FFFFFF",
                    })}
                  </span>

                  {!collapsed && (
                    <span className="nav-label">{item.label}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {!collapsed && (
          <div className="sidebar-footer">
            <p className="footer-text">Admin Dashboard v1.0.5</p>
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;
